// client/src/components/pages/Settings/UserManagement.tsx (converted from .jsx, ST-00216)
//
// ST-00224 (the IAM permission keys are enforced): each action is shown to the holder
// of its key, not only to administrators (administrators hold every key):
//   - users.invite        Invite User
//   - users.assign_roles  Edit Roles
//   - users.manage        Activate / Deactivate, Unlock Sign-in, API access
//   - users.remove        Remove User
// The row actions appear only on a person the server marks `manageable`: someone with
// no administrator role whose permissions are all ones the caller holds (an
// administrator: everyone but a Lex-Adapt global admin). A role in the pickers is
// offered only when `assignable` (its permissions are all ones the caller holds).
// Reset Password and Manage Tenants stay global-admin only. Holders of users.view and
// none of the action keys get a read-only list.
//
// ST-00222 (audit batch IAM):
//   - IAM-3  a tenant administrator gets no actions on a Lex-Adapt global admin account.
//   - IAM-16 an invite whose email already has an account shows that account and asks
//            before adding it (the server answers 409 user_exists); a taken username
//            is an error.
//   - IAM-5  a global admin edits and invites with the roles of the user's own
//            organisation; roles that do not belong there are named before saving.
//   - server error messages are shown instead of generic ones.
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ComponentType,
  type FormEvent,
  type InputHTMLAttributes,
} from 'react';
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaUserCheck,
  FaUnlock,
  FaUserTimes,
  FaEdit,
  FaKey,
  FaBuilding,
  FaEllipsisV,
} from 'react-icons/fa';
import PasswordInputJs from '../../common/PasswordInput';
import api from '../../../services/api';
import ThemedSelectJs from '../../common/ThemedSelect';
import { useAuth } from '../../../context/AuthContext';
import type { ThemedSelectProps } from '../../../types/commonComponents';
import { formatAppDate } from '../../../utils/dateUtils';
import type {
  DataEnvelope,
  ExistingAccount,
  ManagedUser,
  NewUserPayload,
  RoleOption,
  TenantOption,
  TenantUsageResponse,
  TenantUserUsage,
  UserApiError,
  UserListParams,
  UserTenantMembership,
} from '../../../types/clientPages/userManagement';

// Typed views of the JS components (ThemedSelect takes a numeric tenant id here).
const ThemedSelect = ThemedSelectJs as unknown as ComponentType<
  Omit<ThemedSelectProps, 'value'> & { value: string | number }
>;
/** Password field with a show/hide toggle; every other prop goes to the <input>. */
const PasswordInput = PasswordInputJs as unknown as ComponentType<
  InputHTMLAttributes<HTMLInputElement> & { tone?: 'light' | 'dark'; wrapperClassName?: string }
>;

/** The server's message from an axios error, else `fallback`. */
const apiMessage = (err: unknown, fallback: string): string => {
  const e = err as UserApiError;
  return e?.response?.data?.message || fallback;
};

/** Why a role cannot be ticked, or null when the caller may give it (ST-00224). */
const roleRefusalHint = (role: RoleOption): string | null => {
  if (role.assignable !== false) return null;
  return role.role_name === 'global-admin'
    ? 'Only a global administrator can give this role'
    : 'Has permissions you do not have';
};

interface RolePickerProps {
  roles: RoleOption[];
  selected: number[];
  onToggle: (roleId: number) => void;
}

/** The role checkboxes of Invite and Edit Roles; roles the caller cannot give are disabled. */
const RolePicker = ({ roles, selected, onToggle }: RolePickerProps) => (
  <>
    {roles.map((role) => {
      const hint = roleRefusalHint(role);
      return (
        <label
          key={role.role_id}
          className={`flex items-center gap-2 ${hint ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          title={hint ?? undefined}
        >
          <input
            type="checkbox"
            checked={selected.includes(role.role_id)}
            disabled={!!hint}
            onChange={() => onToggle(role.role_id)}
            className="rounded border-slate-300 text-primary-500 focus:ring-primary-500 disabled:opacity-50"
          />
          <span className={`text-sm ${hint ? 'text-slate-400' : 'text-slate-700'}`}>
            {role.role_name}
          </span>
          {role.is_system_role === true && (
            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
              System
            </span>
          )}
          {hint && <span className="text-xs text-slate-400 italic ml-auto">{hint}</span>}
        </label>
      );
    })}
  </>
);

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmClass?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: () => void | Promise<void>;
}

// --- Confirmation Dialog ---
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmClass,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${confirmClass || 'bg-red-500 hover:bg-red-600'}`}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface InviteForm {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  roleIds: number[];
  tenantIds: number[];
}

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: NewUserPayload) => Promise<void>;
  roles: RoleOption[];
  allTenants: TenantOption[];
  currentTenantId?: number;
  /** Global admins: the roles of an organisation (its custom roles + the system roles). */
  loadRolesFor?: (tenantId: number) => Promise<RoleOption[]>;
}

// --- Invite User Modal ---
const InviteUserModal = ({
  isOpen,
  onClose,
  onSave,
  roles,
  allTenants,
  currentTenantId,
  loadRolesFor,
}: InviteUserModalProps) => {
  const [formData, setFormData] = useState<InviteForm>({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    roleIds: [],
    tenantIds: [],
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // IAM-16: the account that already owns the email, while the admin decides.
  const [existing, setExisting] = useState<{
    account: ExistingAccount;
    payload: NewUserPayload;
  } | null>(null);
  // IAM-5: a global admin picks roles of the (primary) organisation the user joins.
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>(roles);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        roleIds: [],
        tenantIds: currentTenantId ? [currentTenantId] : [],
      });
      setFormError(null);
      setExisting(null);
    }
  }, [isOpen, currentTenantId]);

  const primaryTenantId = formData.tenantIds.length > 0 ? Math.min(...formData.tenantIds) : null;
  useEffect(() => {
    if (!isOpen) return undefined;
    if (!loadRolesFor || primaryTenantId === null) {
      setRoleOptions(roles);
      return undefined;
    }
    let live = true;
    loadRolesFor(primaryTenantId)
      .then((list) => {
        if (!live) return;
        setRoleOptions(list);
        const offered = new Set(list.map((r) => r.role_id));
        setFormData((prev) => ({
          ...prev,
          roleIds: prev.roleIds.filter((id) => offered.has(id)),
        }));
      })
      .catch(() => {
        if (live) setRoleOptions(roles);
      });
    return () => {
      live = false;
    };
  }, [isOpen, loadRolesFor, primaryTenantId, roles]);

  if (!isOpen) return null;

  const toggleRole = (roleId: number) => {
    setFormData((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const toggleTenant = (tenantId: number) => {
    setFormData((prev) => ({
      ...prev,
      tenantIds: prev.tenantIds.includes(tenantId)
        ? prev.tenantIds.filter((id) => id !== tenantId)
        : [...prev.tenantIds, tenantId],
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.username.trim() || !formData.email.trim() || !formData.password.trim()) {
      setFormError('Username, email, and password are required.');
      return;
    }
    if (formData.roleIds.length === 0) {
      setFormError('Please select at least one role.');
      return;
    }
    if (allTenants.length > 0 && formData.tenantIds.length === 0) {
      setFormError('Please select at least one tenant.');
      return;
    }

    try {
      setSaving(true);
      const userData: NewUserPayload = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        roleIds: formData.roleIds,
      };
      // Send tenantIds array for multi-tenant assignment
      if (allTenants.length > 0 && formData.tenantIds.length > 0) {
        userData.tenantIds = formData.tenantIds;
      }
      await onSave(userData);
    } catch (err) {
      const apiErr = err as UserApiError;
      const data = apiErr?.response?.data;
      if (apiErr?.response?.status === 409 && data?.code === 'user_exists' && data.existingUser) {
        const payload: NewUserPayload = {
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          roleIds: formData.roleIds,
        };
        if (allTenants.length > 0 && formData.tenantIds.length > 0) {
          payload.tenantIds = formData.tenantIds;
        }
        setExisting({ account: data.existingUser, payload });
      } else {
        setFormError(data?.message || apiErr.message || 'Failed to create user.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddExisting = async () => {
    if (!existing) return;
    try {
      setSaving(true);
      setFormError(null);
      await onSave({ ...existing.payload, addExisting: true });
    } catch (err) {
      setExisting(null);
      setFormError(apiMessage(err, 'Failed to add the existing account.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Invite User</h2>
          <p className="text-sm text-slate-600 mt-1">Add a new user to your organization</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          {existing && (
            <div
              className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm space-y-3"
              role="alert"
            >
              <p>
                An account with this email already exists:{' '}
                <strong>{existing.account.username}</strong> ({existing.account.email}).
              </p>
              <p>
                You can add that account to the organisation instead. It keeps its own username and
                password; the username and temporary password typed here are not used.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleAddExisting}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  Add existing account
                </button>
                <button
                  type="button"
                  onClick={() => setExisting(null)}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Change the email
                </button>
              </div>
            </div>
          )}

          {/* Tenant selector - visible only when backend returned tenant list (admin only) */}
          {allTenants.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tenants <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3">
                {allTenants.map((t) => (
                  <label key={t.tenant_id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.tenantIds.includes(t.tenant_id)}
                      onChange={() => toggleTenant(t.tenant_id)}
                      className="rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">{t.tenant_name}</span>
                    <span className="text-xs text-slate-400">({t.tenant_code})</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Select one or more tenants this user will belong to.
                {formData.tenantIds.length > 0 && (
                  <span className="ml-1 font-medium text-slate-600">
                    {formData.tenantIds.length} selected
                  </span>
                )}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="johndoe"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="john@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Temporary Password <span className="text-red-500">*</span>
            </label>
            <PasswordInput
              value={formData.password}
              onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Minimum 12 characters"
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              User will be required to change this password on first login.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Roles <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3">
              <RolePicker roles={roleOptions} selected={formData.roleIds} onToggle={toggleRole} />
              {roleOptions.length === 0 && (
                <p className="text-sm text-slate-400 italic">No roles available</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !!existing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Creating...
                </>
              ) : (
                <>
                  <FaPlus className="text-xs" />
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditRolesModalProps {
  isOpen: boolean;
  user: ManagedUser | null;
  roles: RoleOption[];
  onClose: () => void;
  onSave: (userId: number, roleIds: number[]) => Promise<void>;
}

// --- Edit Roles Modal ---
const EditRolesModal = ({ isOpen, user, roles, onClose, onSave }: EditRolesModalProps) => {
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      // Only roles offered here can stay ticked: a role of another organisation is
      // dropped on save (named below), as the server refuses it (IAM-5).
      const offered = new Set(roles.map((r) => r.role_id));
      const currentIds = user.role_ids
        ? user.role_ids
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id) && offered.has(id))
        : [];
      setSelectedRoleIds([...new Set(currentIds)]);
      setFormError(null);
    }
  }, [isOpen, user, roles]);

  if (!isOpen || !user) return null;

  const offeredNames = new Set(roles.map((r) => r.role_name));
  const notOffered = (user.roles || '')
    .split(',')
    .map((r) => r.trim())
    .filter((r) => r && !offeredNames.has(r));

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleSave = async () => {
    if (selectedRoleIds.length === 0) return;
    try {
      setSaving(true);
      setFormError(null);
      await onSave(user.user_id, selectedRoleIds);
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to update roles.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Edit Roles</h2>
          <p className="text-sm text-slate-600 mt-1">
            {user.first_name} {user.last_name} ({user.email})
          </p>
        </div>

        <div className="p-6">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-3">
              {formError}
            </div>
          )}
          {notOffered.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm mb-3">
              Saving removes {notOffered.length === 1 ? 'a role' : 'roles'} that{' '}
              {notOffered.length === 1 ? 'does' : 'do'} not belong to this user&apos;s organisation:{' '}
              {notOffered.join(', ')}.
            </div>
          )}
          <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3">
            <RolePicker roles={roles} selected={selectedRoleIds} onToggle={toggleRole} />
          </div>

          {selectedRoleIds.length === 0 && (
            <p className="text-xs text-red-500 mt-2">At least one role is required.</p>
          )}

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || selectedRoleIds.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                'Save Roles'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ResetPasswordModalProps {
  isOpen: boolean;
  user: ManagedUser | null;
  onClose: () => void;
  onSave: (userId: number, newPassword: string) => Promise<void>;
}

// --- Reset Password Modal ---
const ResetPasswordModal = ({ isOpen, user, onClose, onSave }: ResetPasswordModalProps) => {
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNewPassword('');
      setFormError(null);
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!newPassword || newPassword.length < 12) {
      setFormError('Password must be at least 12 characters.');
      return;
    }

    try {
      setSaving(true);
      await onSave(user.user_id, newPassword);
    } catch (err) {
      const apiErr = err as UserApiError;
      setFormError(
        apiErr?.response?.data?.message || apiErr.message || 'Failed to reset password.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Reset Password</h2>
          <p className="text-sm text-slate-600 mt-1">
            {user.first_name} {user.last_name} ({user.email})
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              New Temporary Password <span className="text-red-500">*</span>
            </label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Minimum 12 characters"
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              User will be required to change this password on next login.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Resetting...
                </>
              ) : (
                <>
                  <FaKey className="text-xs" />
                  Reset Password
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AddToTenantModalProps {
  isOpen: boolean;
  user: ManagedUser | null;
  allTenants: TenantOption[];
  onClose: () => void;
  onSave: (userId: number, tenantId: number) => Promise<void>;
  onRefresh?: () => void;
}

// --- Add to Tenant Modal ---
const AddToTenantModal = ({
  isOpen,
  user,
  allTenants,
  onClose,
  onSave,
  onRefresh,
}: AddToTenantModalProps) => {
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [currentTenants, setCurrentTenants] = useState<UserTenantMembership[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      setSelectedTenantId(null);
      setFormError(null);
      setLoadingTenants(true);
      api.users
        .getTenants(user.user_id)
        .then((res) => {
          setCurrentTenants((res.data as UserTenantMembership[]) || []);
        })
        .catch((err) => {
          console.error('Error fetching user tenants:', err);
          setCurrentTenants([]);
        })
        .finally(() => setLoadingTenants(false));
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const currentTenantIds = currentTenants.map((t) => t.tenant_id);
  const availableTenants = allTenants.filter((t) => !currentTenantIds.includes(t.tenant_id));

  const handleRemoveFromTenant = async (tenantId: number, tenantName: string) => {
    if (!window.confirm(`Remove ${user.first_name || user.username} from ${tenantName}?`)) return;
    try {
      setRemoving(tenantId);
      setFormError(null);
      await api.users.removeFromTenant(user.user_id, tenantId);
      setCurrentTenants((prev) => prev.filter((t) => t.tenant_id !== tenantId));
      if (onRefresh) onRefresh();
    } catch (err) {
      setFormError(
        (err as UserApiError)?.response?.data?.message || 'Failed to remove from tenant.'
      );
    } finally {
      setRemoving(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedTenantId) {
      setFormError('Please select a tenant.');
      return;
    }

    try {
      setSaving(true);
      await onSave(user.user_id, selectedTenantId);
    } catch (err) {
      const apiErr = err as UserApiError;
      setFormError(
        apiErr?.response?.data?.message || apiErr.message || 'Failed to add user to tenant.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Add to Tenant</h2>
          <p className="text-sm text-slate-600 mt-1">
            {user.first_name} {user.last_name} ({user.email})
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          {/* Current tenants */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Current Tenants</label>
            {loadingTenants ? (
              <p className="text-sm text-slate-400">Loading...</p>
            ) : currentTenants.length === 0 ? (
              <p className="text-sm text-slate-400 italic">None</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {currentTenants.map((t) => (
                  <span
                    key={t.tenant_id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {t.tenant_name} ({t.tenant_code})
                    <button
                      type="button"
                      onClick={() => handleRemoveFromTenant(t.tenant_id, t.tenant_name)}
                      disabled={removing === t.tenant_id}
                      className="ml-0.5 text-green-600 hover:text-red-600 hover:bg-red-100 rounded-full w-4 h-4 flex items-center justify-center transition-colors"
                      title={`Remove from ${t.tenant_name}`}
                    >
                      {removing === t.tenant_id ? '...' : '\u00D7'}
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tenant selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Add to Tenant <span className="text-red-500">*</span>
            </label>
            {availableTenants.length === 0 && !loadingTenants ? (
              <p className="text-sm text-slate-400 italic">
                User is already a member of all available tenants.
              </p>
            ) : (
              <ThemedSelect
                value={selectedTenantId || ''}
                onChange={(e) => setSelectedTenantId(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface"
              >
                <option value="">Select a tenant...</option>
                {availableTenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.tenant_name} ({t.tenant_code})
                  </option>
                ))}
              </ThemedSelect>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !selectedTenantId || availableTenants.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Adding...
                </>
              ) : (
                <>
                  <FaBuilding className="text-xs" />
                  Add to Tenant
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Main Component ---
const UserManagement = () => {
  const { tenant, user: currentUser, hasAppRole, hasPermission } = useAuth();
  // ST-00224: each action needs its permission key (administrators hold them all).
  const canInvite = hasPermission('users.invite');
  const canAssignRoles = hasPermission('users.assign_roles');
  const canManageUsers = hasPermission('users.manage');
  const canRemoveUsers = hasPermission('users.remove');
  const hasUserActions = canInvite || canAssignRoles || canManageUsers || canRemoveUsers;
  const isGlobalAdmin = hasAppRole('global-admin');

  // Data state
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [usage, setUsage] = useState<TenantUserUsage | null>(null);
  const [allTenants, setAllTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editRolesUser, setEditRolesUser] = useState<ManagedUser | null>(null);
  // The roles offered in Edit Roles: those of the edited user's organisation (IAM-5).
  const [editRolesOptions, setEditRolesOptions] = useState<RoleOption[]>([]);
  const [resetPasswordUser, setResetPasswordUser] = useState<ManagedUser | null>(null);
  const [addToTenantUser, setAddToTenantUser] = useState<ManagedUser | null>(null);

  // Action menu state
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: '',
    confirmClass: '',
    onConfirm: () => {},
  });

  // Close action menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node | null)) {
        setOpenActionMenu(null);
      }
    };
    if (openActionMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [openActionMenu]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchTerm]);

  // Fetch data
  const fetchUsers = useCallback(async () => {
    try {
      setError(null);
      const params: UserListParams = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await api.users.getAll(params);
      setUsers(
        (response.data as DataEnvelope<ManagedUser>)?.data || (response.data as ManagedUser[]) || []
      );
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    }
  }, [debouncedSearch, statusFilter]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.roles.getAll();
      setRoles(
        (response.data as DataEnvelope<RoleOption>)?.data || (response.data as RoleOption[]) || []
      );
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const response = await api.tenants.getUsage();
      setUsage((response.data as TenantUsageResponse)?.data?.users || null);
    } catch (err) {
      console.error('Error fetching usage:', err);
    }
  }, []);

  const fetchAllTenants = useCallback(async () => {
    try {
      const response = await api.tenants.getAll();
      setAllTenants((response.data as DataEnvelope<TenantOption>)?.data || []);
    } catch (err) {
      // 403 is expected for non-admin users — silently ignore
      if ((err as UserApiError)?.response?.status !== 403) {
        console.error('Error fetching all tenants:', err);
      }
    }
  }, []);

  // Initial load. The role list serves Invite and Edit Roles; the organisation list
  // the global-admin actions.
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchUsers(),
        fetchUsage(),
        ...(canInvite || canAssignRoles ? [fetchRoles()] : []),
        ...(isGlobalAdmin ? [fetchAllTenants()] : []),
      ]);
      setLoading(false);
    };
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Global admins: the roles of one organisation (its custom roles + the system roles).
  const loadRolesFor = useCallback(async (tenantId: number): Promise<RoleOption[]> => {
    const response = await api.roles.getAll({ tenantId });
    return (
      (response.data as DataEnvelope<RoleOption>)?.data || (response.data as RoleOption[]) || []
    );
  }, []);

  const openEditRoles = (user: ManagedUser) => {
    setEditRolesOptions(roles);
    setEditRolesUser(user);
    const userTenantId = user.tenant_ids?.[0];
    if (isGlobalAdmin && userTenantId !== undefined) {
      loadRolesFor(userTenantId)
        .then(setEditRolesOptions)
        .catch((err) => console.error('Error fetching the organisation roles:', err));
    }
  };

  // Refetch users when filters change (after initial load)
  useEffect(() => {
    if (!loading) {
      fetchUsers();
    }
  }, [debouncedSearch, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Handlers ---
  const handleInviteUser = async (userData: NewUserPayload) => {
    await api.users.create(userData);
    setInviteModalOpen(false);
    await Promise.all([fetchUsers(), fetchUsage()]);
  };

  const handleToggleStatus = (user: ManagedUser) => {
    const activating = !user.is_active;
    setConfirmDialog({
      isOpen: true,
      title: activating ? 'Activate User' : 'Deactivate User',
      message: activating
        ? `Are you sure you want to activate ${user.first_name} ${user.last_name}? They will be able to log in again.`
        : `Are you sure you want to deactivate ${user.first_name} ${user.last_name}? They will no longer be able to log in.`,
      confirmLabel: activating ? 'Activate' : 'Deactivate',
      confirmClass: activating
        ? 'bg-green-500 hover:bg-green-600'
        : 'bg-amber-500 hover:bg-amber-600',
      onConfirm: async () => {
        try {
          await api.users.updateStatus(user.user_id, activating);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          await fetchUsers();
        } catch (err) {
          console.error('Error updating user status:', err);
          setError(apiMessage(err, 'Failed to update user status.'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // ST-00215 review 2 (B1): five wrong passwords lock an address for 15 minutes;
  // an administrator can lift that at once.
  const handleUnlock = (user: ManagedUser) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Unlock Sign-in',
      message: `Unlock sign-in for ${user.first_name} ${user.last_name} (${user.email})? Their failed sign-in attempts are cleared and they can sign in again straight away.`,
      confirmLabel: 'Unlock',
      confirmClass: 'bg-green-500 hover:bg-green-600',
      onConfirm: async () => {
        try {
          await api.users.unlock(user.user_id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          await fetchUsers();
        } catch (err) {
          console.error('Error unlocking user:', err);
          setError((err as UserApiError)?.response?.data?.message || 'Failed to unlock the user.');
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleToggleApiAccess = (user: ManagedUser) => {
    const enabling = !user.api_access_enabled;
    setConfirmDialog({
      isOpen: true,
      title: enabling ? 'Enable API Access' : 'Disable API Access',
      message: enabling
        ? `Enable API access for ${user.first_name} ${user.last_name}? They will be able to generate personal OData API keys for Excel / Power BI.`
        : `Disable API access for ${user.first_name} ${user.last_name}? Existing API keys for this user will stop working within a minute. Keys are kept and resume working if re-enabled.`,
      confirmLabel: enabling ? 'Enable' : 'Disable',
      confirmClass: enabling
        ? 'bg-green-500 hover:bg-green-600'
        : 'bg-amber-500 hover:bg-amber-600',
      onConfirm: async () => {
        try {
          await api.users.setApiAccess(user.user_id, enabling);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          await fetchUsers();
        } catch (err) {
          console.error('Error updating API access:', err);
          setError(apiMessage(err, 'Failed to update API access.'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDeleteUser = (user: ManagedUser) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove User',
      message: `Are you sure you want to permanently remove ${user.first_name} ${user.last_name} (${user.email}) from your organization? This action cannot be undone.`,
      confirmLabel: 'Remove',
      confirmClass: 'bg-red-500 hover:bg-red-600',
      onConfirm: async () => {
        try {
          await api.users.delete(user.user_id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          await Promise.all([fetchUsers(), fetchUsage()]);
        } catch (err) {
          console.error('Error deleting user:', err);
          setError(apiMessage(err, 'Failed to remove user.'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Errors are shown in the Edit Roles box (it stays open).
  const handleSaveRoles = async (userId: number, roleIds: number[]) => {
    await api.users.assignRoles(userId, roleIds);
    setEditRolesUser(null);
    await fetchUsers();
  };

  const handleResetPassword = async (userId: number, newPassword: string) => {
    await api.users.resetPassword(userId, newPassword);
    setResetPasswordUser(null);
  };

  const handleAddToTenant = async (userId: number, tenantId: number) => {
    await api.users.addToTenant(userId, tenantId);
    setAddToTenantUser(null);
  };

  // --- Helpers ---
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return formatAppDate(dateStr, { month: 'short' });
  };

  const renderRoleBadges = (user: ManagedUser) => {
    if (!user.roles) return <span className="text-xs text-slate-400 italic">No roles</span>;
    const roleList = user.roles
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    const maxVisible = 2;
    const visible = roleList.slice(0, maxVisible);
    const remaining = roleList.length - maxVisible;
    return (
      <div className="flex flex-wrap gap-1">
        {visible.map((role, idx) => (
          <span
            key={idx}
            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {role}
          </span>
        ))}
        {remaining > 0 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
            +{remaining}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">User Management</h1>
              <p className="text-slate-600 text-sm mt-1">
                {hasUserActions
                  ? 'Manage users, roles, and access within your organization'
                  : 'The users of your organization. You do not have permission to change them.'}
              </p>
            </div>
            {canInvite && (
              <button
                onClick={() => setInviteModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              >
                <FaPlus className="text-sm" />
                Invite User
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start">
            <svg
              className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Users Card */}
        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
          {/* Card Header with Filters */}
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Users</h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  {usage && usage.max != null
                    ? `${usage.current} / ${usage.max} users`
                    : `${users.length} user${users.length !== 1 ? 's' : ''}`}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search users..."
                    className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-56"
                  />
                </div>

                {/* Status Filter */}
                <ThemedSelect
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-surface"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </ThemedSelect>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
                <p className="mt-4 text-slate-600">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                  <svg
                    className="w-8 h-8 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Users Found</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  {debouncedSearch || statusFilter !== 'all'
                    ? 'No users match your current filters. Try adjusting your search or status filter.'
                    : 'Get started by inviting your first user to the organization.'}
                </p>
                {canInvite && !debouncedSearch && statusFilter === 'all' && (
                  <button
                    onClick={() => setInviteModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                  >
                    <FaPlus className="text-sm" />
                    Invite Your First User
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">
                      Roles
                    </th>
                    {allTenants.length > 0 && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden md:table-cell">
                        Tenants
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden lg:table-cell">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden lg:table-cell">
                      Last Login
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider w-12">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-slate-200">
                  {users.map((user) => {
                    // ST-00224: the server says whether the caller may act on this person
                    // (IAM-3: a Lex-Adapt global admin account only for a global admin).
                    // An older server without the field: everyone but a global admin.
                    const manageable = user.manageable ?? (isGlobalAdmin || !user.is_global_admin);
                    const isSelf = currentUser?.userId === user.user_id;
                    const canEditRolesRow = canAssignRoles && manageable;
                    const canManageRow = canManageUsers && manageable;
                    // Nobody can deactivate or remove their own account (the server says 400).
                    const canToggleStatusRow = canManageRow && !isSelf;
                    const canRemoveRow = canRemoveUsers && manageable && !isSelf;
                    const canGlobalRow = allTenants.length > 0 && manageable;
                    const upperItems = canEditRolesRow || canGlobalRow;
                    // Unlock, status and API access (users.manage); Remove (users.remove).
                    const lowerItems = canManageRow || canRemoveRow;
                    const hasRowActions = upperItems || lowerItems;
                    return (
                      <tr key={user.user_id} className="hover:bg-slate-50 transition-colors">
                        {/* Combined User column: name + email + mobile-only badges */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 uppercase">
                              {user.first_name?.[0] || ''}
                              {user.last_name?.[0] || ''}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-slate-900 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-xs text-slate-500 truncate">{user.email}</div>
                              {/* Show status badge inline on small screens */}
                              <div className="flex items-center gap-2 mt-1 lg:hidden">
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                    user.is_active
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                                {user.is_locked && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    Locked
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Roles */}
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {canEditRolesRow ? (
                            <button
                              onClick={() => openEditRoles(user)}
                              className="group flex items-center gap-1.5 cursor-pointer"
                              title="Click to edit roles"
                            >
                              {renderRoleBadges(user)}
                              <FaEdit className="text-slate-400 group-hover:text-primary-500 text-xs flex-shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ) : (
                            renderRoleBadges(user)
                          )}
                        </td>

                        {/* Tenants */}
                        {allTenants.length > 0 && (
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex flex-wrap gap-1">
                              {(user.tenants || '')
                                .split(', ')
                                .filter(Boolean)
                                .map((code) => (
                                  <span
                                    key={code}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700"
                                  >
                                    {code}
                                  </span>
                                ))}
                            </div>
                          </td>
                        )}

                        {/* Status */}
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {user.is_locked && (
                            <span
                              className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                              title="Too many failed sign-in attempts. Unlock from the actions menu, or it clears by itself after 15 minutes."
                            >
                              Locked
                            </span>
                          )}
                        </td>

                        {/* Last Login */}
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="text-sm text-slate-600">
                            {formatDate(user.last_login_date)}
                          </div>
                        </td>

                        {/* Actions - kebab dropdown */}
                        <td className="px-4 py-3 text-right">
                          {!manageable && hasUserActions && user.is_global_admin && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600"
                              title="Lex-Adapt support account. Only Lex-Adapt can change it."
                            >
                              Lex-Adapt
                            </span>
                          )}
                          {!manageable && hasUserActions && !user.is_global_admin && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500"
                              title="This person has access you do not have (an administrator role, or permissions you do not hold). Only someone with at least their access, such as an administrator, can change them."
                            >
                              Outside your access
                            </span>
                          )}
                          {hasRowActions && (
                            <div
                              className="relative inline-block"
                              ref={openActionMenu === user.user_id ? actionMenuRef : undefined}
                            >
                              <button
                                onClick={() =>
                                  setOpenActionMenu(
                                    openActionMenu === user.user_id ? null : user.user_id
                                  )
                                }
                                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                title="Actions"
                              >
                                <FaEllipsisV className="text-sm" />
                              </button>
                              {openActionMenu === user.user_id && (
                                <div className="absolute right-0 mt-1 w-48 bg-surface rounded-lg shadow-lg border border-slate-200 py-1 z-30">
                                  {canEditRolesRow && (
                                    <button
                                      onClick={() => {
                                        openEditRoles(user);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                    >
                                      <FaEdit className="text-slate-400 text-xs" />
                                      Edit Roles
                                    </button>
                                  )}
                                  {canGlobalRow && (
                                    <>
                                      <button
                                        onClick={() => {
                                          setResetPasswordUser(user);
                                          setOpenActionMenu(null);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                      >
                                        <FaKey className="text-purple-500 text-xs" />
                                        Reset Password
                                      </button>
                                      <button
                                        onClick={() => {
                                          setAddToTenantUser(user);
                                          setOpenActionMenu(null);
                                        }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                      >
                                        <FaBuilding className="text-primary-500 text-xs" />
                                        Manage Tenants
                                      </button>
                                    </>
                                  )}
                                  {upperItems && lowerItems && (
                                    <div className="border-t border-slate-100 my-1"></div>
                                  )}
                                  {canManageRow && user.is_locked && (
                                    <button
                                      onClick={() => {
                                        handleUnlock(user);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
                                    >
                                      <FaUnlock className="text-xs" /> Unlock Sign-in
                                    </button>
                                  )}
                                  {canToggleStatusRow && (
                                    <button
                                      onClick={() => {
                                        handleToggleStatus(user);
                                        setOpenActionMenu(null);
                                      }}
                                      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                                        user.is_active
                                          ? 'text-amber-700 hover:bg-amber-50'
                                          : 'text-green-700 hover:bg-green-50'
                                      }`}
                                    >
                                      {user.is_active ? (
                                        <>
                                          <FaUserTimes className="text-xs" /> Deactivate
                                        </>
                                      ) : (
                                        <>
                                          <FaUserCheck className="text-xs" /> Activate
                                        </>
                                      )}
                                    </button>
                                  )}
                                  {canManageRow && (
                                    <button
                                      onClick={() => {
                                        handleToggleApiAccess(user);
                                        setOpenActionMenu(null);
                                      }}
                                      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                                        user.api_access_enabled
                                          ? 'text-amber-700 hover:bg-amber-50'
                                          : 'text-green-700 hover:bg-green-50'
                                      }`}
                                    >
                                      <FaKey className="text-xs" />
                                      {user.api_access_enabled
                                        ? 'Disable API Access'
                                        : 'Enable API Access'}
                                    </button>
                                  )}
                                  {canRemoveRow && (
                                    <button
                                      onClick={() => {
                                        handleDeleteUser(user);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                      <FaTrash className="text-xs" />
                                      Remove User
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <InviteUserModal
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSave={handleInviteUser}
        roles={roles}
        allTenants={allTenants}
        currentTenantId={tenant?.tenant_id}
        loadRolesFor={isGlobalAdmin ? loadRolesFor : undefined}
      />

      <EditRolesModal
        isOpen={!!editRolesUser}
        user={editRolesUser}
        roles={editRolesOptions}
        onClose={() => setEditRolesUser(null)}
        onSave={handleSaveRoles}
      />

      <ResetPasswordModal
        isOpen={!!resetPasswordUser}
        user={resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
        onSave={handleResetPassword}
      />

      <AddToTenantModal
        isOpen={!!addToTenantUser}
        user={addToTenantUser}
        allTenants={allTenants}
        onClose={() => setAddToTenantUser(null)}
        onSave={handleAddToTenant}
        onRefresh={fetchUsers}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmClass={confirmDialog.confirmClass}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default UserManagement;
